import { Fragment, useEffect, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { Modal } from '@/components/Modal'
import { MONO, fmt } from './format'
import type { Hierarchy, Settlement, SettlementByCategoryMethod } from '../types'

type MethodKey = 'card' | 'invoiceWeb' | 'invoiceQb' | 'outstanding'

interface SettlementBridgeProps {
  settlement: Settlement
  currency: string
  /** Selected month label, for the drill-down title. */
  month?: string
  /** Optional per-category breakdown per method (order-level export, schema v2). */
  breakdown?: SettlementByCategoryMethod
  /** The classification tree — resolves department / category / stage ids to names. */
  hierarchy?: Hierarchy
}

const TAN = '#AE9056'
const SLATE = '#5E6E8C'
const SLATE_LT = '#8A97AD'

/** True Sale by payment: Settled (card + invoice) + Outstanding. This decomposition
 *  ties every month. Clicking a segment drills into its breakdown by category
 *  (Business unit › Department › Category, with the Planning Center stage). */
export function SettlementBridge({ settlement: s, currency, month, breakdown, hierarchy }: SettlementBridgeProps) {
  const [drill, setDrill] = useState<{ key: MethodKey; label: string; value: number } | null>(null)
  const [openRow, setOpenRow] = useState<string | null>(null)
  useEffect(() => setOpenRow(null), [drill?.key]) // collapse order lists when the segment changes
  // id → name lookups from the hierarchy (fall back to the id so nothing renders blank).
  const deptName = new Map<string, string>()
  const catName = new Map<string, string>()
  hierarchy?.units.forEach((u) =>
    u.departments.forEach((d) => {
      deptName.set(d.id, d.name)
      d.categories.forEach((c) => catName.set(c.id, c.name))
    }),
  )
  const stageName = new Map((hierarchy?.stages ?? []).map((st) => [st.id, st.name]))
  const rowLabel = (r: { department: string; category: string; stage: string | null }) =>
    `${deptName.get(r.department) ?? r.department} › ${catName.get(r.category) ?? r.category}`
  const total = s.trueSales || 1
  const parts: { key: string; methodKey: MethodKey; value: number; color: string }[] = [
    { key: 'Card', methodKey: 'card', value: s.card, color: TAN },
    { key: 'Invoice (web)', methodKey: 'invoiceWeb', value: s.invoiceWeb, color: SLATE },
    ...(s.invoiceQb > 0 ? [{ key: 'Invoice (QuickBooks)', methodKey: 'invoiceQb' as MethodKey, value: s.invoiceQb, color: SLATE_LT }] : []),
    { key: 'Outstanding', methodKey: 'outstanding' as MethodKey, value: s.outstanding, color: 'transparent' },
  ]
  const pct = (v: number) => Math.round((v / total) * 100)
  const drillRows = drill ? (breakdown?.[drill.key] ?? []) : []

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
      <div className="flex items-baseline justify-between">
        <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
          True Sale · by payment
          <InfoPopover term="splitByPayment" />
        </h3>
        <span className={cn('text-sm text-stone-500 dark:text-stone-400', MONO)}>{fmt(s.trueSales, currency)}</span>
      </div>
      <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
        Settled (card + invoice) + Outstanding — ties every month
      </p>

      {/* Stacked bar — each segment is clickable, mirroring the legend below */}
      <div className="mt-4 flex h-9 w-full overflow-hidden rounded-lg ring-1 ring-stone-900/5 dark:ring-white/10">
        {parts.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => p.value > 0 && setDrill({ key: p.methodKey, label: p.key, value: p.value })}
            aria-label={`${p.key} ${fmt(p.value, currency)}`}
            title={`${p.key}: ${fmt(p.value, currency)}`}
            className={cn(
              'h-full transition-opacity hover:opacity-80',
              p.value > 0 ? 'cursor-pointer' : 'cursor-default',
              p.key === 'Outstanding' &&
                'bg-[repeating-linear-gradient(45deg,#E7E1D6_0_6px,#F2EEE6_6px_12px)] dark:bg-[repeating-linear-gradient(45deg,#3a3530_0_6px,#2a2622_6px_12px)]',
            )}
            style={{
              width: `${(p.value / total) * 100}%`,
              backgroundColor: p.color === 'transparent' ? undefined : p.color,
            }}
          />
        ))}
      </div>

      {/* Legend — click a payment line to drill into its breakdown by category */}
      <div className={cn('mt-4 grid gap-3', parts.length === 4 ? 'grid-cols-4' : 'grid-cols-3')}>
        {parts.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => p.value > 0 && setDrill({ key: p.methodKey, label: p.key, value: p.value })}
            className={cn(
              'rounded-md p-1 text-left transition-colors',
              p.value > 0 ? 'cursor-pointer hover:bg-champagne-100/60 dark:hover:bg-champagne-500/10' : 'cursor-default',
            )}
          >
            <div className="flex items-center gap-1.5">
              <span
                className={cn('h-2.5 w-2.5 shrink-0 rounded-sm', p.key === 'Outstanding' && 'bg-stone-300 dark:bg-stone-600')}
                style={{ backgroundColor: p.key === 'Outstanding' ? undefined : p.color }}
                aria-hidden
              />
              <span className="text-xs text-stone-500 dark:text-stone-400">{p.key}</span>
            </div>
            <p className={cn('mt-1 text-sm font-semibold', MONO)}>{fmt(p.value, currency)}</p>
            <p className="text-[11px] text-stone-400 dark:text-stone-500">{pct(p.value)}%</p>
          </button>
        ))}
      </div>

      {/* Settled vs outstanding summary */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg bg-stone-50 px-3 py-2 text-xs dark:bg-stone-800/50">
        <span className="inline-flex items-center gap-1 text-stone-500 dark:text-stone-400">
          Settled <span className={cn('font-medium text-stone-700 dark:text-stone-200', MONO)}>{fmt(s.cashCollected, currency)}</span> ({pct(s.cashCollected)}%)
          <InfoPopover term="cashCollected" />
        </span>
        <span className="inline-flex items-center gap-1 text-stone-500 dark:text-stone-400">
          Outstanding <span className={cn('font-medium text-stone-700 dark:text-stone-200', MONO)}>{fmt(s.outstanding, currency)}</span> ({pct(s.outstanding)}%)
          <InfoPopover term="outstanding" />
        </span>
      </div>

      {/* Gross → net memo line */}
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-stone-400 dark:text-stone-500">
        <span className="inline-flex items-center gap-1">Gross Sale <span className={MONO}>{fmt(s.grossSale, currency)}</span> <InfoPopover term="grossSale" /></span>
        <span aria-hidden>·</span>
        <span className="inline-flex items-center gap-1">− PC credit (memo) <span className={MONO}>{fmt(s.pcCredit, currency)}</span> <InfoPopover term="pcCreditConsumed" /></span>
        <span aria-hidden>·</span>
        <span className="text-stone-500 dark:text-stone-300">True Sale <span className={cn('font-medium', MONO)}>{fmt(s.trueSales, currency)}</span></span>
        <span aria-hidden>·</span>
        <span className="inline-flex items-center gap-1">− Refunds <span className={MONO}>{fmt(s.refunds, currency)}</span> <InfoPopover term="refunds" /></span>
        <span aria-hidden>·</span>
        <span className="inline-flex items-center gap-1 text-stone-500 dark:text-stone-300">Net Sale <span className={cn('font-medium', MONO)}>{fmt(s.netSales, currency)}</span> <InfoPopover term="netSale" /></span>
      </div>

      <Modal
        open={!!drill}
        onClose={() => setDrill(null)}
        eyebrow={`${drill?.label ?? ''}${month ? ` · ${month}` : ''}`}
        title={drill ? fmt(drill.value, currency) : ''}
        subtitle={drill?.key === 'outstanding' ? 'Ordered this month, not yet settled — by category' : 'Settled by category'}
        footer={
          !drillRows.length
            ? 'Breakdown by category appears here once the order-level export is supplied for this month (each order tagged with its payment method and category).'
            : drill?.key === 'invoiceQb'
              ? 'QuickBooks accrual — Lv 3 retainer (acct 42400) + Fractional CMO (42450). Billed through QuickBooks, not the website, so there is no per-order detail.'
              : drill?.key === 'outstanding'
                ? 'Orders placed this month, not yet settled (paid) — regardless of invoice status. The collections view. Click a category to see the orders behind it (clinic · value · age in days).'
                : 'Settled value by Department › Category (Planning Center rows also show the stage), allocated across each order’s lines pro-rata to True-Sale line value.'
        }
      >
        {drillRows.length ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-700 dark:text-stone-500">
                <th className="pb-2 pr-3 text-left font-medium">Department › Category</th>
                <th className="pb-2 pl-3 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {drillRows.map((r) => {
                const canExpand = drill?.key === 'outstanding' && (r.orders?.length ?? 0) > 0
                const open = canExpand && openRow === r.categoryKey
                const stageTag = r.stage ? (
                  <span className="ml-1.5 rounded-full bg-stone-100 px-1.5 py-px text-[10px] font-medium text-stone-500 dark:bg-stone-800 dark:text-stone-400">
                    {stageName.get(r.stage) ?? r.stage}
                  </span>
                ) : null
                return (
                  <Fragment key={r.categoryKey}>
                    <tr className="border-b border-stone-50 dark:border-stone-800/60">
                      <td className="py-2.5 pr-3 text-stone-700 dark:text-stone-200">
                        {canExpand ? (
                          <button
                            type="button"
                            onClick={() => setOpenRow(open ? null : r.categoryKey)}
                            aria-expanded={open}
                            className="inline-flex items-center gap-1.5 text-left transition-colors hover:text-stone-900 dark:hover:text-white"
                          >
                            <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-stone-400 transition-transform', open && 'rotate-90')} />
                            {rowLabel(r)}
                            {stageTag}
                            <span className="text-xs text-stone-400">· {r.orders!.length} {r.orders!.length === 1 ? 'order' : 'orders'}</span>
                          </button>
                        ) : (
                          <>
                            {rowLabel(r)}
                            {stageTag}
                          </>
                        )}
                      </td>
                      <td className={cn('py-2.5 pl-3 text-right', MONO)}>{fmt(r.amount, currency)}</td>
                    </tr>
                    {open &&
                      r.orders!.map((o, i) => (
                        <tr key={i} className="bg-stone-50/60 dark:bg-stone-800/30">
                          <td className="py-1.5 pl-6 pr-3">
                            <span className="text-xs text-stone-600 dark:text-stone-300">{o.client}</span>
                            <span
                              className={cn(
                                'ml-1.5 text-[11px]',
                                o.ageDays > 60
                                  ? 'text-red-600 dark:text-red-400'
                                  : o.ageDays > 30
                                    ? 'text-amber-600 dark:text-amber-400'
                                    : 'text-stone-400 dark:text-stone-500',
                              )}
                              title="days since the order was placed"
                            >
                              · {o.ageDays}d
                            </span>
                          </td>
                          <td className={cn('py-1.5 pl-3 text-right text-xs text-stone-600 dark:text-stone-300', MONO)}>{fmt(o.value, currency)}</td>
                        </tr>
                      ))}
                  </Fragment>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-300 dark:border-stone-600">
                <td className="pt-3 font-semibold">Total</td>
                <td className={cn('pt-3 pl-3 text-right font-bold', MONO)}>{fmt(drill?.value ?? 0, currency)}</td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <p className="rounded-lg bg-stone-50 px-3.5 py-4 text-xs leading-relaxed text-stone-500 dark:bg-stone-800/50 dark:text-stone-400">
            The current export carries only the monthly {drill?.label.toLowerCase()} total
            {' '}(<span className={MONO}>{fmt(drill?.value ?? 0, currency)}</span>). The breakdown by category — and the underlying orders — appear here once the order‑level export is supplied for this month.
          </p>
        )}
      </Modal>
    </div>
  )
}
